import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';

import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import AssetActions from 'components/AssetActions';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import LoadingOverlay from 'components/LoadingOverlay';
import { ProcessType } from 'const';
import { getImageResolution } from 'utils/getImageResolution';
import { ImageBoxProps } from './models';
import styles from './styles.module.scss';

const ImageBox: React.FunctionComponent<ImageBoxProps> = (props) => {
  const {
    container,
    showImageDetails,
    deleteImage,
    image,
    imageCount,
    isActive,
    isDragging,
    isImageBeingUploaded,
    isSelected,
    onImageClick,
    dataTestId = 'imageBox',
  } = props;

  const id = image.get('id');
  const name = image.get('name');
  const number = image.get('number');
  const documentId = image.get('documentId');
  const internalImageInfo = image.get('_internalInfo');
  const imageSource = internalImageInfo?.get('source');
  const resolution = getImageResolution(internalImageInfo);
  const product = image.get('product');
  const productName = product?.first();

  const onClick = (): void => {
    if (onImageClick !== undefined) {
      onImageClick(id, documentId, image);
    }
  };

  return (
    <div className={styles.ImageBox} ref={container} data-testid={dataTestId}>
      <AccessibleDiv
        className={classNames(styles.box, {
          [styles.box__active]: isActive,
          [styles.box__draggable]: isDragging,
          [styles.box__selected]: isSelected,
        })}
        onClick={onClick}
        data-testid={`${dataTestId}-button`}
      >
        <LoadingOverlay isLocked={isImageBeingUploaded} activeProcessType={ProcessType.DOCUMENT_UPLOADING} spinnerSize="md"
          data-testid={`${dataTestId}-button-loadingOverlay`}/>
        {!isDragging &&
          <>
            {imageSource
              ? <img alt={imageSource} className={styles.image} src={imageSource} data-testid={`${dataTestId}-button-img`}/>
              : <div className={styles.icon} data-testid={`${dataTestId}-button-icon`}>
                <Icon type={IconType.IMAGE} size="md" color="disabled" dataTestId={`${dataTestId}-button`} />
              </div>
            }
            {
              !isImageBeingUploaded &&
              <AssetActions
                assetClickable={!!onImageClick}
                onPreviewOpen={showImageDetails}
                onDelete={deleteImage}
                dataTestId={`${dataTestId}-button-assetActions`}
              />
            }
            {_.isFinite(imageCount) && !isImageBeingUploaded &&
              <div className={styles.counter} data-testid={`${dataTestId}-button-imageCount`}>{imageCount}</div>
            }
          </>
        }
      </AccessibleDiv>
      <div className={styles.info} data-testid={`${dataTestId}-text`}>
        <div className={styles.primary} data-testid={`${dataTestId}-text-name`}>{name}</div>
        <div className={styles.secondary} data-testid={`${dataTestId}-text-resolution`}>{resolution}</div>
        <div className={styles.number} data-testid={`${dataTestId}-text-number`}>{number}</div>
        {productName && <div className={styles.product} data-testid={`${dataTestId}-text-productName`}>{productName}</div>}
      </div>
    </div>
  );
};

export default ImageBox;
